import React, { useState, useEffect, useRef } from 'react';
import Header from '../Header/Header';
import { Icon, CustomTable, Search, Pagination, Loader, ICONS, Select } from '@ohif/ui';
import { useTheme } from "../../ui/src/contextProviders";
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { capitalizeWords, customStyle, formatDate, formatTime, getCurrencySymbol } from '@custom/Utils/utils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { DatePickerWithRange } from '@ohif/ui-next';
import { Form, Formik } from 'formik';
import billingAuditList from '@custom/rtk-apis/audit/billingAuditLogs';
import { useNavigate } from 'react-router-dom';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';

const BillingAudit = () => {
    const billingAuditListResponse = useSelector((state: RootState) => state.billingAuditListResponse);
    const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);
    const dispatch = useDispatch<AppDispatch>();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const [searchQuery, setSearchQuery] = useState('');
    const [tableData, setTableData] = useState([]);
    const [pgNumber, setPgNumber] = useState(1);
    const [pgLimit, setPgLimit] = useState(30);
    const [search, setSearch] = useState('');
    const [totalPg, setTotalPg] = useState(1000000);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [selectedCount, setSelectedCount] = useState(0);
    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
        startDate: null,
        endDate: null,
    });
    const [selectedTimeFrame, setSelectedTimeFrame] = useState(null);
    const buttonRef = useRef(null);
    const formikRef = useRef(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const dateRangeOptions = [
        { label: 'Today', value: 'current_date' },
        { label: 'Last One Week', value: 'past_1_week' },
        { label: 'Last One Month', value: 'past_1_month' },
        { label: 'Last One Year', value: '1_year' },
    ];
    const [timeFrameOptions, setTimeFrameOptions] = useState(dateRangeOptions);

    const handleTimeFrameChange = (selectedOption) => {
        setSelectedTimeFrame(selectedOption);
        setDateRange({ startDate: '', endDate: '' }); // Clear date range when timeframe is selected
    };

    useEffect(() => {
        dispatch(getBillingGeneralSettings());
    }, [])

    const columns = [
        { key: 'name', title: 'Name', sort: true },
        { key: 'patientId', title: 'Patient ID' },
        { key: 'gender', title: 'Gender' },
        { key: 'contactNumber', title: 'Contact Number', sort: true },
        { key: 'invoiceNumber', title: 'Invoice Number', sort: true },
        { key: 'cancelReason', title: 'Cancel Reason' },
        { key: 'status', title: 'Status' },
        { key: 'paymentMode', title: 'Payment Mode' },
        { key: 'amount', title: 'Amount' },
        { key: 'dateTime', title: 'Date/Time' },
    ];
    const actions = [];
    const searchClick = (searchText) => {
        setPgNumber(1);
        setSearch(searchText);
        setSearchInputValue(searchText);
        fetchAuditData({ search: searchText, pageNumber: 1, limit: pgLimit });
    };

    const onPageChangeHandler = (pg) => {
        setPgNumber(pg);
        fetchAuditData({ pageNumber: pg });
    };

    const handlePageSizeChange = (e) => {
        setPgLimit(e);
        fetchAuditData({ limit: e });
    };

    const resetFilter = () => {
        setSearch('');
        setSearchInputValue('');
        setDateRange({ startDate: '', endDate: '' });
        setSelectedTimeFrame(null);
        setPgNumber(1);
        if (formikRef.current) {
            formikRef.current.resetForm();
            formikRef.current.setFieldValue('timeFrame', '');
        }
        setIsFilterOpen(false);
        fetchAuditData({});
    };
    const handleResetForm = () => {
        setSelectedTimeFrame(null);
        setPgNumber(1);
        if (formikRef.current) {
            formikRef.current.resetForm();
            formikRef.current.setFieldValue('timeFrame', '');
        }
        fetchAuditData({});
    }

    const filterFormInitialValue = {
        timeFrame: selectedTimeFrame?.value || '',
    };

    const handleSaveForm = (values) => {
        setIsFilterOpen(false);
        setDateRange({ startDate: '', endDate: '' }); // Clear date range when timeframe is applied
        fetchAuditData({ dateRange: selectedTimeFrame?.value });
    };

    const handleDateChange = (dates: { startDate: string, endDate: string }) => {
        const { startDate, endDate } = dates;
        if (startDate && endDate) {
            setDateRange({ startDate, endDate });
            setSelectedTimeFrame(null); // Clear timeframe when date range is selected
            setPgNumber(1);
            fetchAuditData({ startDate, endDate, pageNumber: 1, limit: pgLimit });
        }
    };

    const modifyDate = (date?: string) => {
        if (!date) return "";
        // Transform "DDMMYYYY" to "YYYY-MM-DD"
        return `${date?.slice(4, 8)}-${date?.slice(2, 4)}-${date?.slice(0, 2)}`;
    };

    const fetchAuditData = (params = {}) => {
        const hostpialId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
        const queryParams = {
            hospitalId: hostpialId,
            limit: pgLimit,
            pageNumber: pgNumber,
            search: search,
            ...params, // Override with any additional params
        };

        if (Object.keys(params).length > 0 && selectedTimeFrame && !dateRange.startDate && !dateRange.endDate) {
            queryParams.dateRange = selectedTimeFrame.value;
        } else if (queryParams.startDate && queryParams.endDate) {
            const changedStartDate = modifyDate(queryParams.startDate);
            const changedEndDate = modifyDate(queryParams.endDate);
            queryParams.startDate = changedStartDate;
            queryParams.endDate = changedEndDate;
        }

        dispatch(billingAuditList(queryParams));
    };

    const downloadAuditPDF = () => {
        const doc = new jsPDF();
        doc.text(`Billing Audit Trails (${selectedTimeFrame ? selectedTimeFrame.label : dateRange.startDate && dateRange.endDate ? `${modifyDate(dateRange.startDate)} to ${modifyDate(dateRange.endDate)}` : `Overall`})`, 14, 15);
        const pdfColumns = columns.map(col => col.title);
        const pdfData = tableData.map(row => columns.map(col => row[col.key]));
        doc.autoTable({
            head: [pdfColumns],
            body: pdfData,
            startY: 20,
            theme: 'grid',
            styles: { fontSize: 8 },
            headStyles: { fillColor: [41, 128, 185] },
        });
        doc.save('Billing_audit_trails.pdf');
    };

    const handleDownload = async () => {
        const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
        let obj: any = {
            hospitalId,
            limit: 100000,
            pageNumber: 1,
            search: search,
            from: "DOWNLOAD",
        };
        if (selectedTimeFrame && !dateRange.startDate && !dateRange.endDate) {
            obj.dateRange = selectedTimeFrame.value;
        } else if (!selectedTimeFrame && dateRange.startDate && dateRange.endDate) {
            const changedStartDate = modifyDate(dateRange?.startDate);
            const changedEndDate = modifyDate(dateRange?.endDate);
            obj.startDate = changedStartDate;
            obj.endDate = changedEndDate;
        }
        await dispatch(billingAuditList(obj));
        if (billingAuditListResponse.status === 'success') {
            downloadAuditPDF();
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '--';
        const date = new Date(timestamp);
        return `${formatDate(date)} ${formatTime(date)}`;
    };

    useEffect(() => {
        if (billingAuditListResponse.status === 'success') {
            setTotalPg(billingAuditListResponse?.pagination?.totalCount || 0);
            const currencySymbol = getCurrencySymbol(billingGeneralSettingsData?.result?.currency);
            const tableArray = billingAuditListResponse?.result?.map((item) => {
                let dateTime;
                const displayStatus = item?.editedFields?.canceledBillDetails?.statusOfBill
                    ? (item.editedFields.canceledBillDetails.statusOfBill === 'Refund' ? 'Refund (Paid)' : item.editedFields.canceledBillDetails.statusOfBill)
                    : item?.status
                    ? (item.status === 'Refund' ? 'Refund (Paid)' : item.status)
                    : (item?.invoiceDetials?.invoiceStatus || '--');

                switch (displayStatus) {
                    case 'Paid':
                        dateTime = formatTimestamp(item?.invoiceDetials?.paidAt);
                        break;
                    case 'Subsidy':
                        dateTime = formatTimestamp(item?.invoiceDetials?.paidAt);
                    case 'Refund (Paid)':
                        dateTime = formatTimestamp(item?.editedFields?.canceledBillDetails?.canceledAt);
                    case 'Refund (Unpaid)':
                        dateTime = formatTimestamp(item?.invoiceDetials?.canceledBillDetails?.canceledAt);
                        break;
                    default:
                        dateTime = '--';
                }

                const paymentMode = displayStatus === 'Refund (Unpaid)' || displayStatus === 'Refund Unpaid'
                ? '--'
                : (() => {
                    const rawMode = item?.paymentMode || item?.invoiceDetials?.modeOfPayment || '--';
                    if (rawMode === '--' || rawMode === 'PayTM') {
                        return rawMode;
                    }
                    return rawMode.replace(/([a-z])([A-Z])/g, '$1 $2');
                })();

                return {
                    name: item?.name ? capitalizeWords(item.name) : '--',
                    patientId: item?.patientId || '--',
                    gender: item?.gender || '--',
                    contactNumber: item?.contactNumber || '--',
                    invoiceNumber: item?.invoiceNumber ? item.invoiceNumber.replace(/-/g, '') : '--',
                    status: displayStatus,
                    paymentMode: paymentMode,
                    cancelReason: item?.reasonOfCancel ? capitalizeWords(item.reasonOfCancel) : '--',
                    amount: displayStatus === 'Paid'
                    ? `${currencySymbol}${item?.amount?.toFixed(2)}` : displayStatus === 'Subsidy' ? `${currencySymbol}${item?.amount?.toFixed(2)}`
                    : displayStatus === 'Refund (Paid)'
                        ? (() => {
                                const totalCanceledPrice = item?.editedFields?.currentCanceledBillItems?.[0]?.canceledBillDetails?.totalCanceledPrice;
                                if (totalCanceledPrice) {
                                    const formattedAmount = totalCanceledPrice.toFixed(2);
                                    return formattedAmount.endsWith('.00')
                                        ? `${currencySymbol}${Math.floor(totalCanceledPrice)}`
                                        : `${currencySymbol}${formattedAmount}`;
                                }
                                return '--';
                            })()
                        : '--',
                    dateTime: dateTime,
                };
            });

            setTableData(tableArray || []);
        }
    }, [billingAuditListResponse.status, billingAuditListResponse.result, billingGeneralSettingsData?.result?.currency]);

    useEffect(() => {
        fetchAuditData(); // Initial fetch
    }, []);

    const handleBack = () => {
        navigate('/invoice');
    };

    return (
        <>
            <div className='page page__fullBody' style={isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}>
                <Header isBack={true} title='Audit Trails' handleIsBackFunctionality={handleBack}></Header>
                <div className='page__body'>
                    <div className='tableWrap'>
                        <div className='tableWrap__head'>
                            <div className="formRow formRow__3 m_0 w_100 formRow--flexEnd">
                                <div className='formCol'>
                                    <Search text="Name" onSearchClick={searchClick} value={searchInputValue} setValue={setSearchInputValue}></Search>
                                </div>
                                <div className='formCol'>
                                    <DatePickerWithRange
                                        className="px-4 py-1 rounded h-10"
                                        onChange={handleDateChange}
                                        startPlaceholder="From"
                                        endPlaceholder="To"
                                        startDate={dateRange.startDate}
                                        endDate={dateRange.endDate}
                                        style={{ width: '200px' }}
                                    />
                                </div>
                                <div className="formCol" style={{ position: 'relative' }}>
                                    {selectedCount > 0 && (
                                        <span className="badge absolute top-0 left-0 translate-x-1/2 translate-y-1/2">
                                            {selectedCount}
                                        </span>
                                    )}
                                    <div className='btn__group'>
                                        <button
                                            ref={buttonRef}
                                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                                            className="filter-button relative"
                                        >
                                            <span className='filterIcon'><img src={ICONS.filterIcon} alt="down arrow" /></span> Filter
                                        </button>
                                        <button onClick={resetFilter} className="filter-button">
                                            <span className='filterIcon'><Icon name="ClearFilters" className="text-black cursor-pointer" width="16" height="16"></Icon></span>
                                            Clear
                                        </button>
                                    </div>
                                    {isFilterOpen && (
                                        <div
                                            className="filter-popup"
                                            style={{
                                                position: 'absolute',
                                                top: buttonRef.current.offsetHeight + 10,
                                                left: 0,
                                                zIndex: 100,
                                               backgroundColor: "#ffffff",
                                                padding: '16px',
                                                borderRadius: "16px",
                                                width: "360px",
                                                boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.16)',
                                            }}
                                        >
                                            <Formik
                                                key={JSON.stringify(selectedTimeFrame)}
                                                initialValues={filterFormInitialValue}
                                                onSubmit={(values, { setSubmitting }) => {
                                                    handleSaveForm(values);
                                                    setSubmitting(false);
                                                }}
                                            >
                                                {({ values, setFieldValue, handleSubmit }) => (
                                                    <Form onSubmit={handleSubmit}>
                                                        <div className='formRow'>
                                                            <div className='formCol'>
                                                                <div className='form__Field'>
                                                                    <label htmlFor='status' className="form__label">Select Time Frame</label>
                                                                <Select
                                                                    id='timeFrame'
                                                                    placeholder='Select'
                                                                    className='custom-bg-white'
                                                                    isClearable={false}
                                                                    isSearchable={true}
                                                                    closeMenuOnSelect={false}
                                                                    hideSelectedOptions={false}
                                                                    options={timeFrameOptions}
                                                                    value={selectedTimeFrame}
                                                                    onChange={handleTimeFrameChange}
                                                                />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className='filter-bottom'>
                                                            <button
                                                                type="submit"
                                                                className='custom-button main-theme mr-2'
                                                            >
                                                                Apply
                                                            </button>
                                                            <button type="button" className='custom-button' onClick={handleResetForm}>Clear</button>

                                                        </div>
                                                    </Form>
                                                )}
                                            </Formik>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='btn__group'>
                                <button className='btn btn__white' style={{ borderRadius: '50px' }} onClick={handleDownload}>
                                    Download PDF
                                </button>
                            </div>
                        </div>
                        <div className='tableContainer'>
                            <CustomTable
                                columns={columns}
                                data={tableData}
                                actions={actions}
                                searchQuery={searchQuery}
                                from="audit"
                                userDetails={''}
                                setClickedItem={''}
                                clickedItem={''}
                            />
                        </div>
                    </div>
                </div>
                {tableData.length > 0 && <Pagination
                    onChangePage={onPageChangeHandler}
                    onChangePerPage={handlePageSizeChange}
                    totalPage={totalPg}
                    currentPage={pgNumber}
                    perPage={pgLimit}
                />}
                <Loader isLoaderOpen={billingAuditListResponse.loading} />
            </div>
        </>
    );
};

export default BillingAudit;
